import { ReactNode, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import { useReduxForm } from '@rainbow-modules/hooks';
import { showAppNotification } from '@rainbow-modules/app';
import { isEmpty } from '@rainbow-modules/validation';
import HelpMeWrite from 'components/HelpMeWrite';
import MicrophoneIcon from 'components/icons/microphone';
import DictateForm from 'components/SendNoteForm/DictateForm';
import useMultipartMutation from 'data/hooks/useMultipartMutation';
import {
    Container,
    StyledLabel,
    InputWrapper,
    StyledTextarea,
    ActionsContainer,
    ActionButton,
    DictateFormContainer,
    DictateFormLabel,
} from './styled';

type TextareaWithActionsProps = {
    agentId?: string;
    rows?: number;
    id?: string;
    className?: string;
    name?: string;
    label?: ReactNode;
    labelAlignment?: 'left' | 'center' | 'right';
    placeholder?: string;
    borderRadius?: 'square' | 'semi-square' | 'semi-rounded' | 'rounded';
    disabled?: boolean;
    required?: boolean;
    error?: ReactNode;
    value?: string;
    onChange?: (value: string) => void;
};

const TextareaWithActions = ({
    agentId: agentIdInProps,
    rows,
    id,
    className,
    name,
    label,
    labelAlignment = 'left',
    placeholder,
    borderRadius = 'semi-square',
    disabled = false,
    required,
    error,
    value,
    onChange: onChangeProp,
    ...fieldProps
}: TextareaWithActionsProps) => {
    const {
        value: valueInProps = '',
        onChange = () => {},
    } = useReduxForm({
        id,
        className,
        name,
        label,
        labelAlignment,
        placeholder,
        borderRadius,
        disabled,
        required,
        error,
        value,
        onChange: onChangeProp,
        rows,
        ...fieldProps,
    });

    const { agentId: agentIdFromParams = '' } = useParams();
    const agentId = agentIdInProps || agentIdFromParams;
    const [isDictating, setIsDictating] = useState(false);

    const {
        isLoading: isTranscribing,
        mutateAsync: transcribeAudio,
    } = useMultipartMutation<FormData, { transcription?: string }>({
        method: 'POST',
        pathname: `/agents/${agentId}/transcribe`,
    });

    const disableElements = isTranscribing || disabled;

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange(event.target.value);
        },
        [onChange],
    );

    const handleInsert = useCallback(
        (text: string) => {
            onChange(text);
        },
        [onChange],
    );

    const handleDictateAccept = useCallback(async (blob: Blob | null) => {
        setIsDictating(false);
        if (!blob) return;

        try {
            const formData = new FormData();
            formData.append('audio', blob);
            const { transcription } = await transcribeAudio({
                body: formData,
            });
            if (transcription) {
                onChange(transcription);
            }
        } catch (err) {
            const message = (err as Error)?.message || 'Something went wrong. Please try again.';
            showAppNotification({
                title: 'Error',
                description: message,
                icon: 'error',
                timeout: 5000,
            });
        }
    }, [onChange, transcribeAudio]);

    const handleDictateClick = useCallback(() => setIsDictating(true), []);
    const handleDictateCancel = useCallback(() => setIsDictating(false), []);

    if (isDictating) {
        return (
            <Container className={className}>
                <RenderIf isTrue={!!label}>
                    <DictateFormLabel>{label}</DictateFormLabel>
                </RenderIf>
                <DictateFormContainer>
                    <DictateForm
                        onAccept={handleDictateAccept}
                        onCancel={handleDictateCancel}
                    />
                </DictateFormContainer>
            </Container>
        );
    }

    return (
        <Container className={className}>
            <RenderIf isTrue={!!label}>
                <StyledLabel>{label}</StyledLabel>
            </RenderIf>
            <InputWrapper>
                <StyledTextarea
                    id={id}
                    name={name}
                    labelAlignment={labelAlignment}
                    placeholder={placeholder}
                    borderRadius={borderRadius}
                    disabled={disableElements}
                    required={required}
                    error={error}
                    value={valueInProps}
                    onChange={handleChange}
                    rows={rows}
                />
                <ActionsContainer>
                    <ActionButton
                        tooltip="Dictate"
                        icon={<MicrophoneIcon />}
                        onClick={handleDictateClick}
                        disabled={disableElements}
                    />
                    <RenderIf isTrue={!isEmpty(valueInProps)}>
                        <HelpMeWrite
                            value={valueInProps}
                            onInsert={handleInsert}
                            agentId={agentId}
                            disabled={disableElements}
                        />
                    </RenderIf>
                </ActionsContainer>
            </InputWrapper>
        </Container>
    );
};

export default TextareaWithActions;
